import { NO, UIDialogView, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { populatedValuesOfReferences, referenceId } from "../../Core/RHReference"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHDatePicker } from "../../Custom components/RH/RHDatePicker"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHVerticalStackView } from "../../Custom components/RH/RHVerticalStackView"
import { RHColors, RHRadius, RHSpacing, RHButtons } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import type { RHProcurementOffer, RHProcurementPartOffer, RHReconciliationCandidateCompany } from "../../SocketClientFunctions"


type ReconciliationCandidateCompany = RHReconciliationCandidateCompany & {
    offerPrices: (RHProcurementPartOffer["price"] | null)[]
}


// ─────────────────────────────────────────────────────────────────────────────
// "Loo täpsustav hange" — the single dialog that replaces opening the full
// procurement editor for a reconciliation round. Selecting offerer companies,
// picking a deadline, and writing the explanatory broadcast message all
// happen here; submitting creates the child procurement already published,
// with the team/COI confirmations copied from the parent, the selected
// companies auto-registered as tenderers, and the message broadcast to them
// immediately — see CreateAndPublishReconciliationProcurement.
// ─────────────────────────────────────────────────────────────────────────────

class ReconciliationCandidateRow extends UIView {

    readonly checkbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { style: { overflow: "hidden" } },
            style: { overflow: "hidden" }
        })
        .addedAsSubviewToView(this)

    readonly priceLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textAlignment: UITextView.textAlignment.right,
            textColor: RHColor.textSecondary,
            style: { fontSize: "13px" }
        })
        .addedAsSubviewToView(this)

    candidate!: ReconciliationCandidateCompany
    lowestPriceForPart?: RHProcurementPartOffer["price"]

    configureWithCandidate(candidate: ReconciliationCandidateCompany) {
        this.candidate = candidate

        this.checkbox.configuredWithObject({
            titleLabel: { text: RHTextSanitizer.escapedHTML(candidate.name || candidate.registryCode || "Tundmatu ettevõte") },
            selected: YES
        })
        this.priceLabel.text = candidate.offerPrices.map(price => {
            if (price === null) {
                return "Hinda ei õnnestunud laadida"
            }
            const priceText = RHEuroFormatter.formattedCurrency(price.amount, { currency: price.currency })
            if (price.amount === this.lowestPriceForPart?.amount) {
                return `${priceText}<br>Madalaim pakkumus`
            }
            return priceText
        }).join("<br>")
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        bounds
            .IF(bounds.width < 440)
                .rectangleWithHeight(RHButtons.compactHeight)
                .assignedAsFrameOfView(this.checkbox)
                .rectangleForNextRow(RHSpacing.small, this.priceLabel)
            .ELSE()
                .rectangleWithWidth(Math.max(0, bounds.width - 160 - RHSpacing.small))
                .rectangleWithHeight(RHButtons.compactHeight)
                .assignedAsFrameOfView(this.checkbox)
                .rectangleForNextColumn(RHSpacing.small, 160)
                .rectangleWithHeight(this.priceLabel)
            .ENDIF()
            .assignedAsFrameOfView(this.priceLabel)
    }

}


export class CreateReconciliationProcurementDialogView extends UIDialogView {

    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHColors.white,
            style: {
                borderRadius: `${RHRadius.large}px`,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06)"
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Loo täpsustav hange",
            textColor: RHColor.textPrimary
        })
        .addedAsSubviewToView(this.contentView)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColor.textSecondary,
            style: { fontSize: "14px", whiteSpace: "normal" }
        })
        .addedAsSubviewToView(this.contentView)

    readonly candidatesHeaderLabel = new UITextView()
        .configuredWithObject({
            text: "Kaasatavad pakkujad",
            textColor: RHColor.textPrimary,
            style: { fontSize: "14px", fontWeight: "600" }
        })
        .addedAsSubviewToView(this.contentView)

    readonly candidatesContainerView = new RHVerticalStackView()
        .configuredWithObject({ rowGap: RHSpacing.small })
        .addedAsSubviewToView(this.contentView)

    readonly candidatesStatusLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColor.textSecondary,
            style: { fontSize: "14px" }
        })

    readonly deadlineLabel = new UITextView()
        .configuredWithObject({
            text: "Pakkumuste esitamise tähtaeg",
            textColor: RHColor.textPrimary,
            style: { fontSize: "14px", fontWeight: "600" }
        })
        .addedAsSubviewToView(this.contentView)

    readonly deadlineDatePicker = new RHDatePicker()
        .configuredWithObject({
            type: "datetime",
            minDate: new Date(),
            placeholder: "Vali kuupäev ja kellaaeg",
            isRequired: YES
        })
        .addedAsSubviewToView(this.contentView)

    readonly messageLabel = new UITextView()
        .configuredWithObject({
            text: "Selgitus pakkujatele",
            textColor: RHColor.textPrimary,
            style: { fontSize: "14px", fontWeight: "600" }
        })
        .addedAsSubviewToView(this.contentView)

    readonly messageTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Selgita pakkujatele, miks see täpsustav hange loodi..."
        })
        .addedAsSubviewToView(this.contentView)

    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({ alertType: RHAlertBannerView.type.Error })

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loobu" },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loo ja avalda hange" },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    void this._submit()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [this.deadlineDatePicker, this.messageTextArea]
        for (const row of this._candidateRows) {
            items.push(row.checkbox)
        }
        items.push(this.cancelButton, this.submitButton)
        return items
    })

    _procurementId = ""
    _partId = ""
    _candidateRows: ReconciliationCandidateRow[] = []
    _didCreateChild?: (childProcurementId: string) => void
    _isSubmitting = NO
    _candidateLoadGeneration = 0

    constructor(elementID?: string) {
        super(elementID)
        this.view = this.contentView
        this.dismissesOnTapOutside = NO
    }


    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }

    override dismiss(animated?: boolean) {
        super.dismiss(animated)
        RHPageFocusManager.shared.popLayer()
        this._candidateLoadGeneration += 1
    }


    /** Loads this part's actual bidders and shows the dialog. `partNumber` is display-only, for the heading text. */
    async showForPart(
        procurementId: string,
        partId: string,
        partNumber: number,
        didCreateChild: (childProcurementId: string) => void
    ) {
        const candidateLoadGeneration = ++this._candidateLoadGeneration
        this._procurementId = procurementId
        this._partId = partId
        this._didCreateChild = didCreateChild
        this.explanationLabel.text = `Vali osa ${partNumber} pakkujate seast need, keda soovid täpsustavasse hankesse kaasata.`
        this.deadlineDatePicker.clear()
        this.messageTextArea.text = ""
        this.errorBanner.removeFromSuperview()
        this._loadCandidateRows([])
        this.candidatesStatusLabel.text = "Laadin pakkujaid…"
        this.candidatesStatusLabel.addedAsSubviewToView(this.candidatesContainerView)

        this.showInRootView(YES)
        this.setNeedsLayout()

        try {
            const candidates = await RHCore.sharedInstance.procurementStore
                .retrieveReconciliationCandidatesForPart(procurementId, partId)
            if (candidateLoadGeneration !== this._candidateLoadGeneration) {
                return
            }
            if (candidates.length === 0) {
                this.candidatesStatusLabel.text = "Sellele hankeosale ei ole kaasatavaid pakkujaid."
                this.setNeedsLayoutUpToRootView()
                return
            }
            let offers: RHProcurementOffer[] = []
            try {
                offers = await RHCore.sharedInstance.offerStore.retrieveIncomingOffersFromCompaniesWithIdsForProcurement(
                    candidates.map(candidate => candidate.companyId),
                    procurementId
                )
            }
            catch (error) {
                console.error("CreateReconciliationProcurementDialogView: failed to load offer prices:", error)
            }
            if (candidateLoadGeneration !== this._candidateLoadGeneration) {
                return
            }
            this._loadCandidateRows(candidates.map(candidate => {
                const partOffers = offers
                    .filter(offer => offer.status === "submitted" && referenceId(offer.offererCompany) === candidate.companyId)
                    .flatMap(offer => populatedValuesOfReferences(offer.partOffers))
                    .filter(partOffer => referenceId(partOffer.partId) === partId && !partOffer.isCancelled)
                const offerPrices = partOffers.map(partOffer => {
                    if (partOffer._decryptionFailed || !Number.isFinite(partOffer.price?.amount) ||
                        partOffer.price.amount < 0 || !/^[A-Z]{3}$/i.test(partOffer.price.currency)) {
                        return null
                    }
                    return partOffer.price
                })
                if (offerPrices.length === 0) {
                    offerPrices.push(null)
                }
                return { ...candidate, offerPrices }
            }))
        }
        catch (error) {
            if (candidateLoadGeneration !== this._candidateLoadGeneration) {
                return
            }
            console.error("CreateReconciliationProcurementDialogView: failed to load candidates:", error)
            this.candidatesStatusLabel.removeFromSuperview()
            this._showError("Pakkujate laadimine ebaõnnestus.")
        }
    }


    _loadCandidateRows(candidates: ReconciliationCandidateCompany[]) {
        this.candidatesStatusLabel.removeFromSuperview()
        this._candidateRows.everyElement.removeFromSuperview()

        let lowestPriceForPart: RHProcurementPartOffer["price"] | undefined
        for (const price of candidates.flatMap(candidate => candidate.offerPrices)) {
            if (price === null) {
                lowestPriceForPart = undefined
                this.candidatesStatusLabel.text = "Madalaimat pakkumust ei saa määrata, sest kõiki hindu ei õnnestunud laadida."
                this.candidatesStatusLabel.addedAsSubviewToView(this.candidatesContainerView)
                break
            }
            if (lowestPriceForPart && price.currency.toUpperCase() !== lowestPriceForPart.currency.toUpperCase()) {
                lowestPriceForPart = undefined
                this.candidatesStatusLabel.text = "Madalaimat pakkumust ei saa määrata, sest hinnad on eri valuutades."
                this.candidatesStatusLabel.addedAsSubviewToView(this.candidatesContainerView)
                break
            }
            if (!lowestPriceForPart || price.amount < lowestPriceForPart.amount) {
                lowestPriceForPart = price
            }
        }

        this._candidateRows = candidates.map(candidate => {
            const row = new ReconciliationCandidateRow()
                .configuredWithObject({ lowestPriceForPart })
                .addedAsSubviewToView(this.candidatesContainerView)
            row.configureWithCandidate(candidate)
            return row
        })
        this.submitButton.enabled = this._candidateRows.length > 0 && !this._isSubmitting
        this.setNeedsLayoutUpToRootView()
    }


    _selectedCompanyIds(): string[] {
        return this._candidateRows
            .filter(row => row.checkbox.selected)
            .map(row => row.candidate.companyId)
    }


    _showError(text: string) {
        this.errorBanner.configureBannerWithObject({ text })
        this.errorBanner.addedAsSubviewToView(this.contentView)
        this.setNeedsLayoutUpToRootView()
    }


    async _submit() {
        const selectedCompanyIds = this._selectedCompanyIds()
        if (selectedCompanyIds.length === 0) {
            this._showError("Vali vähemalt üks pakkuja.")
            return
        }
        if (!this.deadlineDatePicker.selectedDate) {
            this._showError("Vali pakkumuste esitamise tähtaeg.")
            return
        }
        if (this.messageTextArea.text.trim().length === 0) {
            this._showError("Kirjuta pakkujatele selgitav sõnum.")
            return
        }

        this.errorBanner.removeFromSuperview()
        this._isSubmitting = YES
        this.submitButton.enabled = NO
        this.submitButton.titleLabel.text = "Loomine..."
        this.setNeedsLayoutUpToRootView()

        try {
            const child = await RHCore.sharedInstance.procurementStore.createAndPublishReconciliationProcurement({
                procurementId: this._procurementId,
                partId: this._partId,
                invitedCompanyIds: selectedCompanyIds,
                deadlineDate: this.deadlineDatePicker.selectedDate,
                broadcastMessage: this.messageTextArea.text
            })
            this._didCreateChild?.(child._id + "")
            this.dismiss()
        }
        catch (error) {
            console.error("CreateReconciliationProcurementDialogView: failed to create reconciliation procurement:", error)
            let errorText = "Täpsustava hanke loomine ebaõnnestus."
            if (error instanceof Error) {
                errorText = error.message
            }
            this._showError(errorText)
        }
        finally {
            this._isSubmitting = NO
            this.submitButton.enabled = YES
            this.submitButton.titleLabel.text = "Loo ja avalda hange"
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const dialogBounds = this.bounds
        const dialogWidth = Math.min(560, dialogBounds.width * 0.9)

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const padding = RHSpacing.medium
        const bounds = this.contentView.contentBoundsWithInset(padding)

        let current = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding * 0.5, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)

        current = current
            .IF(this.errorBanner.superview === this.contentView)
                .rectangleForNextRow(padding * 0.5, this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
            .ENDIF()

        current = current
            .rectangleForNextRow(padding, this.candidatesHeaderLabel)
            .assignedAsFrameOfView(this.candidatesHeaderLabel)

        current = current
            .rectangleForNextRow(padding * 0.5, this.candidatesContainerView)
            .assignedAsFrameOfView(this.candidatesContainerView)
            .rectangleForNextRow(padding, this.deadlineLabel)
            .assignedAsFrameOfView(this.deadlineLabel)
            .rectangleForNextRow(padding * 0.5, padding * 2)
            .assignedAsFrameOfView(this.deadlineDatePicker)
            .rectangleForNextRow(padding, this.messageLabel)
            .assignedAsFrameOfView(this.messageLabel)
            .rectangleForNextRow(padding * 0.5, 96)
            .assignedAsFrameOfView(this.messageTextArea)

        const footerRow = current.rectangleForNextRow(padding, RHButtons.compactHeight)
        const cancelWidth = this.cancelButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 + RHSpacing.large
        const submitWidth = this.submitButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 + RHSpacing.large

        footerRow
            .rectangleWithWidth(cancelWidth + submitWidth + RHSpacing.small, 0.5)
            .distributeViewsAlongWidth(
                [this.cancelButton, this.submitButton],
                undefined,
                RHSpacing.small,
                [cancelWidth, submitWidth]
            )

        const finalHeight = footerRow.max.y + padding
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(Math.min(finalHeight, dialogBounds.height * 0.9), 0.5)
    }

}
